export interface HomeConfig {
  currentValue: number;
  mortgageBalance: number;
  interestRate: number;        // Annual percentage, e.g., 3.5 for 3.5%
  loanTermYears: number;
  propertyTaxRate: number;    // Annual percentage, e.g., 1.2
  annualInsurance: number;
  monthlyHoa: number;
  buildingRatio: number;      // Ratio of value that is depreciable building vs land (e.g. 0.8)
}

export interface RentalConfig {
  monthlyGrossRent: number;
  vacancyRate: number;           // Percentage, e.g., 5.0
  propertyManagementRate: number; // Percentage of gross rent, e.g., 8.0
  maintenanceReserveRate: number;  // Percentage of gross rent, e.g., 5.0
  marginalTaxRate: number;      // Income tax bracket %, e.g., 24.0
}

export interface KeepAndRentFinancing {
  fundingSource: 'CASH' | 'HELOC';
  cashSavingsAllocated: number;
  helocInterestRate: number;    // Annual %, e.g., 8.5
  helocTermYears: number;       // e.g., 10
  helocMaxLtv: number;          // Max LTV allowed on Home #1 (%)
}

export interface MacroConfig {
  home1AppreciationRate: number; // Annual %
  home2AppreciationRate: number; // Annual %
  rentGrowthRate: number;       // Annual %
  expenseInflationRate: number; // Annual %
  investmentReturnRate: number; // Annual % earned on excess unallocated liquid cash
}

export interface SaleConfig {
  sellingCostRate: number;       // Closing fees %, e.g. 6.0
  filingStatus: 'SINGLE' | 'MARRIED'; // Section 121 exclusion: $250k single / $500k married
  capitalGainsTaxRate: number;   // Federal rate %, e.g. 15.0
  originalCostBasis: number;     // Purchase price + improvements
}

export interface ModelConfig {
  home1: HomeConfig;
  home2PurchasePrice: number;
  home2InterestRate: number;
  home2LoanTermYears: number;
  home2PropertyTaxRate: number;
  home2AnnualInsurance: number;
  home2MonthlyHoa: number;
  home2DownPaymentPct: number;    // Target down payment %, e.g. 20.0
  rental: RentalConfig;
  keepFinancing: KeepAndRentFinancing;
  macro: MacroConfig;
  sale: SaleConfig;
}

export interface MonthlyCashFlowResult {
  grossRent: number;
  vacancyLoss: number;
  effectiveRent: number;
  pmFee: number;
  maintenanceReserve: number;
  monthlyPiti: number;
  operatingExpenses: number;
  monthlyInterest: number;
  monthlyDepreciation: number;
  taxImpact: number;
  netMonthlyCashFlow: number;
}

export interface SaleNetProceedsResult {
  salePrice: number;
  sellingCosts: number;
  netSalesPrice: number;
  capitalGain: number;
  exclusion: number;
  taxableGain: number;
  capitalGainsTax: number;
  remainingMortgagePaid: number;
  netCashProceeds: number;
}

export interface TrajectoryYearSnapshot {
  year: number;
  sellBuy: {
    home2Value: number;
    home2MortgageBalance: number;
    home2Equity: number;
    liquidInvestments: number;
    netWorth: number;
    monthlyPiti: number;
  };
  keepRent: {
    home1Value: number;
    home1MortgageBalance: number;
    home1Equity: number;
    home2Value: number;
    home2MortgageBalance: number;
    home2Equity: number;
    helocBalance: number;
    liquidInvestments: number;
    netWorth: number;
    home1MonthlyNetCashFlow: number;
    monthlyPiti: number;
  };
  netWorthDifference: number; // keepRent.netWorth - sellBuy.netWorth
}

export interface SimulationResult {
  saleSummary: SaleNetProceedsResult;
  keepFinancingSummary: {
    h2DownPayment: number;
    cashUsed: number;
    helocUsed: number;
    monthlyHelocPayment: number;
  };
  trajectory: TrajectoryYearSnapshot[];
}

/**
 * Calculates exact monthly mortgage principal & interest (P&I) payment.
 */
export function calcMonthlyPayment(principal: number, annualRatePct: number, termYears: number): number {
  if (principal <= 0 || termYears <= 0) return 0;
  const monthlyRate = (annualRatePct / 100) / 12;
  const numPayments = termYears * 12;
  if (monthlyRate === 0) return principal / numPayments;
  return (monthlyRate * principal) / (1 - Math.pow(1 + monthlyRate, -numPayments));
}

/**
 * Single month amortization calculation.
 */
export function calcAmortizationMonth(
  principal: number,
  annualRatePct: number,
  monthlyPayment: number
): { interestPaid: number; principalPaid: number; remainingBalance: number } {
  if (principal <= 0) return { interestPaid: 0, principalPaid: 0, remainingBalance: 0 };
  const monthlyRate = (annualRatePct / 100) / 12;
  const interestPaid = principal * monthlyRate;
  const principalPaid = Math.min(principal, Math.max(0, monthlyPayment - interestPaid));
  const remainingBalance = Math.max(0, principal - principalPaid);
  return { interestPaid, principalPaid, remainingBalance };
}

/**
 * Monthly rental cash flow calculation for Home #1.
 */
export function calcHome1RentalCashFlow(
  home1: HomeConfig,
  rental: RentalConfig,
  currentHome1Value: number,
  currentMortgageBalance: number
): MonthlyCashFlowResult {
  const grossRent = rental.monthlyGrossRent;
  const vacancyLoss = grossRent * (rental.vacancyRate / 100);
  const effectiveRent = grossRent - vacancyLoss;

  const pmFee = grossRent * (rental.propertyManagementRate / 100);
  const maintenanceReserve = grossRent * (rental.maintenanceReserveRate / 100);

  const monthlyTax = (currentHome1Value * (home1.propertyTaxRate / 100)) / 12;
  const monthlyInsurance = home1.annualInsurance / 12;
  const piPayment = calcMonthlyPayment(currentMortgageBalance, home1.interestRate, home1.loanTermYears);
  const monthlyPiti = piPayment + monthlyTax + monthlyInsurance + home1.monthlyHoa;

  const operatingExpenses = pmFee + maintenanceReserve + monthlyTax + monthlyInsurance + home1.monthlyHoa;

  // Annual Depreciation tax shield: (Building Value) / 27.5 years
  const buildingValue = currentHome1Value * home1.buildingRatio;
  const monthlyDepreciation = (buildingValue / 27.5) / 12;

  // Taxable rental income/loss = Effective Rent - PM - Maint - PropTax - Ins - HOA - Interest - Depreciation
  const monthlyInterest = currentMortgageBalance * ((home1.interestRate / 100) / 12);
  const taxableMonthlyIncome = effectiveRent - (pmFee + maintenanceReserve + monthlyTax + monthlyInsurance + home1.monthlyHoa + monthlyInterest + monthlyDepreciation);

  const taxImpact = taxableMonthlyIncome * (rental.marginalTaxRate / 100);
  const netMonthlyCashFlow = effectiveRent - operatingExpenses - piPayment - taxImpact;

  return {
    grossRent,
    vacancyLoss,
    effectiveRent,
    pmFee,
    maintenanceReserve,
    monthlyPiti,
    operatingExpenses,
    monthlyInterest,
    monthlyDepreciation,
    taxImpact,
    netMonthlyCashFlow
  };
}

/**
 * Calculates net sales proceeds from Home #1 considering Section 121 exclusion.
 */
export function calcSaleNetProceeds(home1: HomeConfig, sale: SaleConfig): SaleNetProceedsResult {
  const salePrice = home1.currentValue;
  const sellingCosts = salePrice * (sale.sellingCostRate / 100);
  const netSalesPrice = salePrice - sellingCosts;

  const costBasis = sale.originalCostBasis > 0 ? sale.originalCostBasis : home1.currentValue * 0.75;
  const capitalGain = netSalesPrice - costBasis;

  const exclusion = sale.filingStatus === 'MARRIED' ? 500000 : 250000;
  const taxableGain = Math.max(0, capitalGain - exclusion);
  const capitalGainsTax = taxableGain * (sale.capitalGainsTaxRate / 100);

  const netCashProceeds = netSalesPrice - home1.mortgageBalance - capitalGainsTax;

  return {
    salePrice,
    sellingCosts,
    netSalesPrice,
    capitalGain,
    exclusion,
    taxableGain,
    capitalGainsTax,
    remainingMortgagePaid: home1.mortgageBalance,
    netCashProceeds: Math.max(0, netCashProceeds)
  };
}

/**
 * Full 10-year simulation engine comparing Sell & Buy vs Keep & Rent.
 */
export function simulateScenarios(config: ModelConfig): SimulationResult {
  // Scenario A: Sell & Buy
  const saleInfo = calcSaleNetProceeds(config.home1, config.sale);
  const netProceedsFromHome1 = saleInfo.netCashProceeds;

  const h2Price = config.home2PurchasePrice;
  const targetDownPayment = h2Price * (config.home2DownPaymentPct / 100);
  
  const actualDownPaymentSell = Math.max(targetDownPayment, netProceedsFromHome1);
  const h2LoanSell = Math.max(0, h2Price - actualDownPaymentSell);
  const h2PiSell = calcMonthlyPayment(h2LoanSell, config.home2InterestRate, config.home2LoanTermYears);
  const excessCashSell = Math.max(0, netProceedsFromHome1 - targetDownPayment);

  // Scenario B: Keep & Rent
  const actualDownPaymentKeep = h2Price * (config.home2DownPaymentPct / 100);
  const h2LoanKeep = h2Price - actualDownPaymentKeep;
  const h2PiKeep = calcMonthlyPayment(h2LoanKeep, config.home2InterestRate, config.home2LoanTermYears);

  let helocBalance = 0;
  let helocPi = 0;
  if (config.keepFinancing.fundingSource === 'HELOC') {
    helocBalance = Math.max(0, actualDownPaymentKeep - config.keepFinancing.cashSavingsAllocated);
    helocPi = calcMonthlyPayment(helocBalance, config.keepFinancing.helocInterestRate, config.keepFinancing.helocTermYears);
  }

  // Monthly tracking state
  let valH2Sell = h2Price;
  let balH2Sell = h2LoanSell;
  let invPoolSell = excessCashSell;

  let valH1Keep = config.home1.currentValue;
  let balH1Keep = config.home1.mortgageBalance;
  let valH2Keep = h2Price;
  let balH2Keep = h2LoanKeep;
  let balHelocKeep = helocBalance;
  let invPoolKeep = 0;
  let currentRent = config.rental.monthlyGrossRent;

  const h1Pi = calcMonthlyPayment(config.home1.mortgageBalance, config.home1.interestRate, config.home1.loanTermYears);
  const trajectory: TrajectoryYearSnapshot[] = [];

  for (let month = 1; month <= 120; month++) {
    const mAppH1 = (config.macro.home1AppreciationRate / 100) / 12;
    const mAppH2 = (config.macro.home2AppreciationRate / 100) / 12;
    const mInvRet = (config.macro.investmentReturnRate / 100) / 12;
    const mRentGrow = (config.macro.rentGrowthRate / 100) / 12;

    valH2Sell *= (1 + mAppH2);
    valH1Keep *= (1 + mAppH1);
    valH2Keep *= (1 + mAppH2);
    currentRent *= (1 + mRentGrow);

    // Amortize Sell & Buy
    const amortH2Sell = calcAmortizationMonth(balH2Sell, config.home2InterestRate, h2PiSell);
    balH2Sell = amortH2Sell.remainingBalance;
    invPoolSell *= (1 + mInvRet);

    // Amortize Keep & Rent
    const amortH1Keep = calcAmortizationMonth(balH1Keep, config.home1.interestRate, h1Pi);
    balH1Keep = amortH1Keep.remainingBalance;

    const amortH2Keep = calcAmortizationMonth(balH2Keep, config.home2InterestRate, h2PiKeep);
    balH2Keep = amortH2Keep.remainingBalance;

    if (balHelocKeep > 0) {
      const amortHeloc = calcAmortizationMonth(balHelocKeep, config.keepFinancing.helocInterestRate, helocPi);
      balHelocKeep = amortHeloc.remainingBalance;
    }

    const tempRental: RentalConfig = {
      ...config.rental,
      monthlyGrossRent: currentRent
    };
    const cfH1 = calcHome1RentalCashFlow(config.home1, tempRental, valH1Keep, balH1Keep);
    const netCfMonth = cfH1.netMonthlyCashFlow - helocPi;

    invPoolKeep = (invPoolKeep + netCfMonth) * (1 + mInvRet);

    if (month % 12 === 0) {
      const yr = month / 12;
      const eqH2Sell = valH2Sell - balH2Sell;
      const nwSell = eqH2Sell + invPoolSell;

      const eqH1Keep = valH1Keep - balH1Keep;
      const eqH2Keep = valH2Keep - balH2Keep;
      const nwKeep = eqH1Keep + eqH2Keep + invPoolKeep - balHelocKeep;

      trajectory.push({
        year: yr,
        sellBuy: {
          home2Value: valH2Sell,
          home2MortgageBalance: balH2Sell,
          home2Equity: eqH2Sell,
          liquidInvestments: invPoolSell,
          netWorth: nwSell,
          monthlyPiti: h2PiSell + (valH2Sell * (config.home2PropertyTaxRate / 100) / 12) + (config.home2AnnualInsurance / 12) + config.home2MonthlyHoa
        },
        keepRent: {
          home1Value: valH1Keep,
          home1MortgageBalance: balH1Keep,
          home1Equity: eqH1Keep,
          home2Value: valH2Keep,
          home2MortgageBalance: balH2Keep,
          home2Equity: eqH2Keep,
          helocBalance: balHelocKeep,
          liquidInvestments: invPoolKeep,
          netWorth: nwKeep,
          home1MonthlyNetCashFlow: netCfMonth,
          monthlyPiti: h2PiKeep + (valH2Keep * (config.home2PropertyTaxRate / 100) / 12) + (config.home2AnnualInsurance / 12) + config.home2MonthlyHoa
        },
        netWorthDifference: nwKeep - nwSell
      });
    }
  }

  return {
    saleSummary: saleInfo,
    keepFinancingSummary: {
      h2DownPayment: actualDownPaymentKeep,
      cashUsed: config.keepFinancing.cashSavingsAllocated,
      helocUsed: helocBalance,
      monthlyHelocPayment: helocPi
    },
    trajectory
  };
}

export function getDefaultModelConfig(): ModelConfig {
  return {
    home1: {
      currentValue: 650000,
      mortgageBalance: 380000,
      interestRate: 3.5,
      loanTermYears: 30,
      propertyTaxRate: 1.2,
      annualInsurance: 1800,
      monthlyHoa: 150,
      buildingRatio: 0.8
    },
    home2PurchasePrice: 950000,
    home2InterestRate: 6.5,
    home2LoanTermYears: 30,
    home2PropertyTaxRate: 1.25,
    home2AnnualInsurance: 2400,
    home2MonthlyHoa: 0,
    home2DownPaymentPct: 20.0,
    rental: {
      monthlyGrossRent: 3400,
      vacancyRate: 5.0,
      propertyManagementRate: 8.0,
      maintenanceReserveRate: 5.0,
      marginalTaxRate: 24.0
    },
    keepFinancing: {
      fundingSource: 'HELOC',
      cashSavingsAllocated: 50000,
      helocInterestRate: 8.5,
      helocTermYears: 10,
      helocMaxLtv: 80.0
    },
    macro: {
      home1AppreciationRate: 3.5,
      home2AppreciationRate: 3.5,
      rentGrowthRate: 3.0,
      expenseInflationRate: 2.5,
      investmentReturnRate: 6.0
    },
    sale: {
      sellingCostRate: 6.0,
      filingStatus: 'MARRIED',
      capitalGainsTaxRate: 15.0,
      originalCostBasis: 500000
    }
  };
}
